import React from 'react';
import type { PiV2Plan } from '../../../patient-insurance/typesV2';
import {
  PI_V2_PREAUTH_LABEL,
  PI_V2_ROLE_LABEL,
  piV2CoverageLabel,
  piV2CoverageTone,
  piV2PreauthTone,
} from '../../../patient-insurance/utilsV2';
import { Icon } from '../../Icon/Icon';
import { Pill } from '../../Pill/Pill';
import { Segmented } from '../../Segmented/Segmented';
import { PatientProfileSectionCard } from '../PatientProfileSectionCard';

export interface PatientInsurancePlanDetailProps {
  plans: PiV2Plan[];
  selectedPlanId: string;
  onSelectPlan: (planId: string) => void;
}

/**
 * Coverage, limitations, frequency and pre-auth — for one plan at a time.
 *
 * In v1 these were flat lists sitting beside `primary` and `secondary`, so
 * "Major · 50%" never said whose 50% it was. They now live inside the plan and
 * the switcher picks which plan you are reading.
 *
 * A switcher rather than side-by-side columns because the four blocks have
 * different shapes — categories are rows, limitations are prose, frequency is a
 * table. Only the first survives columnisation, and it stops fitting at three
 * plans. One structure learned once beats three that each work for a different
 * plan count.
 */
export function PatientInsurancePlanDetail({
  plans,
  selectedPlanId,
  onSelectPlan,
}: PatientInsurancePlanDetailProps) {
  const plan = plans.find((p) => p.id === selectedPlanId) ?? plans[0];
  if (!plan) return null;

  const showSwitcher = plans.length > 1;

  return (
    <div className="flex flex-col gap-4">
      {showSwitcher ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-[13px] text-[var(--ink-3)]">
            Coverage detail for{' '}
            <strong className="font-semibold text-[var(--ink-1)]">{plan.carrier}</strong>
          </span>
          <Segmented
            options={plans.map((p) => ({ value: p.id, label: PI_V2_ROLE_LABEL[p.role] }))}
            value={plan.id}
            onChange={(v) => {
              onSelectPlan(v);
            }}
          />
        </div>
      ) : null}

      <div
        className="grid items-start gap-4"
        style={{ gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr)' }}
      >
        <div className="flex min-w-0 flex-col gap-4">
          <PatientProfileSectionCard
            icon="list"
            title="Coverage by category"
            accent="var(--ai-4)"
            subtitle={`${plan.carrier} · ${plan.planType}`}
          >
            <div className="flex flex-col">
              {plan.coverage.map((c, i) => (
                <div
                  key={c.category}
                  className="grid grid-cols-[7.5rem_1fr] items-center gap-3.5 py-[11px]"
                  style={{ borderTop: i > 0 ? '1px solid var(--cream-2)' : 'none' }}
                >
                  <span>
                    <Pill tone={piV2CoverageTone(c.state)}>
                      {piV2CoverageLabel(c.state, c.percent)}
                      {c.deductibleWaived === true ? ' · ded waived' : ''}
                    </Pill>
                  </span>
                  <div className="min-w-0">
                    <div
                      className="text-[13.5px] font-semibold"
                      style={{ color: c.state === 'covered' ? 'var(--ink-0)' : 'var(--ink-2)' }}
                    >
                      {c.category}
                    </div>
                    <div className="mt-px text-[11.5px] text-[var(--ink-3)]">{c.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </PatientProfileSectionCard>

          <PatientProfileSectionCard icon="doc" title="Plan limitations" accent="var(--gold)">
            {plan.limitations.length === 0 ? (
              <p className="m-0 text-[13px] text-[var(--ink-3)]">
                No limitations reported for this plan.
              </p>
            ) : (
              <div className="flex flex-col gap-[11px]">
                {plan.limitations.map((l) => (
                  <div key={l.label} className="flex gap-2.5">
                    <Icon
                      kind="info"
                      size={14}
                      stroke={1.9}
                      style={{ color: 'var(--ink-4)', marginTop: 2, flexShrink: 0 }}
                    />
                    <div className="text-[13px] leading-[1.5]">
                      <strong className="text-[var(--ink-0)]">{l.label}</strong>{' '}
                      <span className="text-[var(--ink-2)]">— {l.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </PatientProfileSectionCard>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <PatientProfileSectionCard
            icon="refresh"
            title="Frequency"
            accent="var(--ai-4)"
            subtitle="When each procedure is next eligible"
          >
            {plan.frequency.length === 0 ? (
              <p className="m-0 text-[13px] text-[var(--ink-3)]">
                No frequency limits reported for this plan.
              </p>
            ) : (
              <div className="flex flex-col gap-3.5">
                {plan.frequency.map((g) => (
                  <div key={g.group}>
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-[12.5px] font-semibold text-[var(--ink-0)]">{g.group}</span>
                      {g.percent != null ? (
                        <span className="font-[var(--font-mono)] text-[10px] text-[var(--ink-4)]">
                          {g.percent}
                        </span>
                      ) : null}
                    </div>
                    {g.rows.map((r, i) => (
                      <div
                        key={r.procedure}
                        className="flex items-start justify-between gap-3 py-2 text-[12px]"
                        style={{ borderTop: i > 0 ? '1px solid var(--cream-2)' : 'none' }}
                      >
                        <div className="min-w-0">
                          <div className="font-medium text-[var(--ink-0)]">{r.procedure}</div>
                          <div className="text-[11px] text-[var(--ink-3)]">{r.limit}</div>
                        </div>
                        <span className="shrink-0">
                          <Pill tone={r.eligible ? 'success' : 'neutral'} dot={r.eligible}>
                            {r.status}
                          </Pill>
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </PatientProfileSectionCard>

          <PatientProfileSectionCard
            icon="preauth"
            title="Pre-authorizations"
            accent="var(--ai-3)"
          >
            {plan.preauth.length === 0 ? (
              <p className="m-0 text-[13px] text-[var(--ink-3)]">
                No pre-authorizations on file for this plan.
              </p>
            ) : (
              <div className="flex flex-col">
                {plan.preauth.map((p, i) => (
                  <div
                    key={p.id}
                    className="flex items-center gap-2.5 py-[11px]"
                    style={{ borderTop: i > 0 ? '1px solid var(--cream-2)' : 'none' }}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-semibold text-[var(--ink-0)]">{p.procedure}</div>
                      <div className="mt-0.5 font-[var(--font-mono)] text-[10.5px] text-[var(--ink-3)]">
                        {p.id} · {p.detail}
                      </div>
                    </div>
                    <Pill tone={piV2PreauthTone(p.status)} dot={p.status === 'approved'}>
                      {PI_V2_PREAUTH_LABEL[p.status]}
                    </Pill>
                  </div>
                ))}
              </div>
            )}
          </PatientProfileSectionCard>
        </div>
      </div>
    </div>
  );
}
